import { addBreadcrumb, reportError } from '@/utils/sentry';

// Global async wrapper types
declare global {
  interface Window {
    captureAsync: <T extends any[], R>(
      fn: (...args: T) => Promise<R>,
      context?: Record<string, any>
    ) => (...args: T) => Promise<R>;
    safeAsync: <T extends any[], R>(
      fn: (...args: T) => Promise<R>,
      context?: Record<string, any>
    ) => (...args: T) => Promise<R | null>;
    retryAsync: <T extends any[], R>(
      fn: (...args: T) => Promise<R>,
      retries?: number,
      delay?: number,
      context?: Record<string, any>
    ) => (...args: T) => Promise<R>;
    timeoutAsync: <T extends any[], R>(
      fn: (...args: T) => Promise<R>,
      timeoutMs?: number,
      context?: Record<string, any>
    ) => (...args: T) => Promise<R>;
    batchAsync: <T, R>(
      items: T[],
      processor: (item: T, index: number) => Promise<R>,
      options?: {
        batchSize?: number;
        concurrency?: number;
        continueOnError?: boolean;
        context?: Record<string, any>;
      }
    ) => Promise<{
      results: (R | null)[];
      errors: { index: number; error: Error }[];
    }>;
  }
}

// Safe async wrapper - returns null on error instead of throwing
export const safeAsync = <T extends any[], R>(
  fn: (...args: T) => Promise<R>,
  context?: Record<string, any>
): ((...args: T) => Promise<R | null>) => {
  return async (...args: T): Promise<R | null> => {
    const functionName = fn.name || 'anonymous';

    try {
      addBreadcrumb(
        `Safe async operation started: ${functionName}`,
        'async',
        'debug'
      );

      const result = await fn(...args);

      addBreadcrumb(
        `Safe async operation completed: ${functionName}`,
        'async',
        'debug'
      );

      return result;
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));

      addBreadcrumb(
        `Safe async operation failed: ${functionName} - ${err.message}`,
        'async',
        'error'
      );

      reportError(err, {
        type: 'safeAsyncError',
        functionName,
        source: 'safeAsyncWrapper',
        ...context,
      });

      return null; // Return null instead of throwing
    }
  };
};

// Auto-retry async wrapper
export const retryAsync = <T extends any[], R>(
  fn: (...args: T) => Promise<R>,
  retries: number = 3,
  delay: number = 1000,
  context?: Record<string, any>
): ((...args: T) => Promise<R>) => {
  return async (...args: T): Promise<R> => {
    const functionName = fn.name || 'anonymous';
    let lastError: Error;

    for (let attempt = 1; attempt <= retries + 1; attempt++) {
      try {
        addBreadcrumb(
          `Retry async attempt ${attempt}/${retries + 1}: ${functionName}`,
          'async',
          'debug'
        );

        const result = await fn(...args);

        if (attempt > 1) {
          addBreadcrumb(
            `Retry async succeeded on attempt ${attempt}: ${functionName}`,
            'async',
            'info'
          );
        }

        return result;
      } catch (error) {
        lastError = error instanceof Error ? error : new Error(String(error));

        addBreadcrumb(
          `Retry async failed attempt ${attempt}: ${functionName} - ${lastError.message}`,
          'async',
          'warning'
        );

        if (attempt <= retries) {
          // Wait before retrying
          await new Promise((resolve) => setTimeout(resolve, delay * attempt));
        }
      }
    }

    // All retries failed
    addBreadcrumb(
      `Retry async exhausted all attempts: ${functionName}`,
      'async',
      'error'
    );

    reportError(lastError!, {
      type: 'retryAsyncError',
      functionName,
      totalAttempts: retries + 1,
      source: 'retryAsyncWrapper',
      ...context,
    });

    throw lastError!;
  };
};

// Timeout wrapper for async operations
export const timeoutAsync = <T extends any[], R>(
  fn: (...args: T) => Promise<R>,
  timeoutMs: number = 30000,
  context?: Record<string, any>
): ((...args: T) => Promise<R>) => {
  return async (...args: T): Promise<R> => {
    const functionName = fn.name || 'anonymous';

    addBreadcrumb(
      `Timeout async started: ${functionName} (${timeoutMs}ms timeout)`,
      'async',
      'debug'
    );

    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => {
        const timeoutError = new Error(
          `Operation timed out after ${timeoutMs}ms`
        );

        addBreadcrumb(
          `Timeout async exceeded: ${functionName}`,
          'async',
          'error'
        );

        reportError(timeoutError, {
          type: 'timeoutAsyncError',
          functionName,
          timeoutMs,
          source: 'timeoutAsyncWrapper',
          ...context,
        });

        reject(timeoutError);
      }, timeoutMs);
    });

    try {
      const result = await Promise.race([fn(...args), timeoutPromise]);

      addBreadcrumb(
        `Timeout async completed: ${functionName}`,
        'async',
        'debug'
      );

      return result;
    } catch (error) {
      if (error instanceof Error && error.message.includes('timed out')) {
        throw error; // Timeout error already handled above
      }

      // Other errors
      const err = error instanceof Error ? error : new Error(String(error));

      addBreadcrumb(
        `Timeout async failed: ${functionName} - ${err.message}`,
        'async',
        'error'
      );

      reportError(err, {
        type: 'timeoutAsyncError',
        functionName,
        source: 'timeoutAsyncWrapper',
        ...context,
      });

      throw error;
    }
  };
};

// Batch async operations with error handling
export const batchAsync = async <T, R>(
  items: T[],
  processor: (item: T, index: number) => Promise<R>,
  options: {
    batchSize?: number;
    concurrency?: number;
    continueOnError?: boolean;
    context?: Record<string, any>;
  } = {}
): Promise<{
  results: (R | null)[];
  errors: { index: number; error: Error }[];
}> => {
  const {
    batchSize = items.length,
    concurrency = 5,
    continueOnError = true,
    context = {},
  } = options;

  addBreadcrumb(
    `Batch async started: ${items.length} items, ${concurrency} concurrency`,
    'async',
    'info'
  );

  const results: (R | null)[] = new Array(items.length).fill(null);
  const errors: { index: number; error: Error }[] = [];

  // Process in batches
  for (let batchStart = 0; batchStart < items.length; batchStart += batchSize) {
    const batchEnd = Math.min(batchStart + batchSize, items.length);
    const batch = items.slice(batchStart, batchEnd);

    addBreadcrumb(
      `Processing batch ${Math.floor(batchStart / batchSize) + 1}: items ${batchStart}-${batchEnd - 1}`,
      'async',
      'debug'
    );

    // Process batch with concurrency limit
    const batchPromises = batch.map(async (item, batchIndex) => {
      const globalIndex = batchStart + batchIndex;

      try {
        const result = await processor(item, globalIndex);
        results[globalIndex] = result;
      } catch (error) {
        const err = error instanceof Error ? error : new Error(String(error));
        errors.push({ index: globalIndex, error: err });

        addBreadcrumb(
          `Batch async item failed [${globalIndex}]: ${err.message}`,
          'async',
          'warning'
        );

        reportError(err, {
          type: 'batchAsyncError',
          itemIndex: globalIndex,
          batchIndex: Math.floor(batchStart / batchSize),
          source: 'batchAsyncWrapper',
          ...context,
        });

        if (!continueOnError) {
          throw err;
        }
      }
    });

    // Limit concurrency
    for (let i = 0; i < batchPromises.length; i += concurrency) {
      const concurrentBatch = batchPromises.slice(i, i + concurrency);
      await Promise.all(concurrentBatch);
    }
  }

  addBreadcrumb(
    `Batch async completed: ${results.filter((r) => r !== null).length}/${items.length} successful, ${errors.length} errors`,
    'async',
    'info'
  );

  return { results, errors };
};

// Global async utilities - these work automatically without imports
export const setupGlobalAsyncUtilities = () => {
  // Make utilities available globally
  window.safeAsync = safeAsync;
  window.retryAsync = retryAsync;
  window.timeoutAsync = timeoutAsync;
  window.batchAsync = batchAsync;
};
